import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Paper, Slide } from '@mui/material';
import useAPI from '../../../api';
import ActivityProgressSummary from './Summary';
import TimelineChart from '../../../Components/Charts/TimelineChart';
import { FullSizeColumn } from '../../../Components/FlexBox';
import { ParentSize } from '@visx/responsive';
import { ACTIVITY_TYPES_ICONS } from '../../../Utils/constants/activityTypes';
import { useAtomValue } from 'jotai';
import { notifyEndCallAtom } from '../../../Atoms/HubAtoms';

const ActivityProgressPopup = function ActivityProgressPopup({
  open,
  onClose,
  customerId,
  campaignId,
  activeCampaigns = [],
  sidebarOpen = false,
}) {
  const API = useAPI();
  // Current period state for 2-week navigation
  const notifyEndCall = useAtomValue(notifyEndCallAtom);
  const [currentPeriod, setCurrentPeriod] = useState(new Date());
  const [events, setEvents] = useState([]);

  const { startDate, endDate, periodLabel } = useMemo(() => {
    const endOfPeriod = new Date(currentPeriod);
    endOfPeriod.setDate(currentPeriod.getDate() + 7);
    endOfPeriod.setHours(23, 59, 59, 999);

    const startOfPeriod = new Date(currentPeriod);
    startOfPeriod.setDate(currentPeriod.getDate() - 7);
    startOfPeriod.setHours(0, 0, 0, 0);

    const label = `${startOfPeriod.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
    })} - ${endOfPeriod.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
    })}`;

    return {
      startDate: startOfPeriod,
      endDate: endOfPeriod,
      periodLabel: label,
    };
  }, [currentPeriod]);

  const handlePeriodChange = (direction) => {
    setCurrentPeriod((prev) => {
      const newDate = new Date(prev);
      if (direction === 'back') {
        newDate.setDate(prev.getDate() - 7); // Go back 1 week
      } else if (direction === 'forward') {
        newDate.setDate(prev.getDate() + 7); // Go forward 1 week
      }
      return newDate;
    });
  };
  const loadEvents = useCallback(() => {
    API.getActivityHistory(
      customerId,
      {
        pageNumber: 1,
        pageSize: 100, // Assuming a max of 100 activities in 2 weeks
        filterExpression: [
          {
            filterBy: 'commercialCampaignId',
            filterValue: campaignId,
            operator: 'eq',
            group: 0,
          },
          {
            filterBy: 'completedOn',
            filterValue: startDate?.toISOString(),
            operator: 'gt',
            group: 0,
          },
          {
            filterBy: 'completedOn',
            filterValue: endDate?.toISOString(),
            operator: 'lt',
            group: 0,
          },
        ],
        sortExpression: { sortValue: 'completedOn', sort: 'ASC' },
      },
      (res) => {
        setEvents(
          res?.map((item) => ({
            id: item?.id,
            date: new Date(item?.completedOn),
            value: item?.campaignAdvancement,
            name: item?.completedByFullName,
            status: item?.activityType,
            color: '#1E88E5',
            icon: ACTIVITY_TYPES_ICONS[item?.activityType],
          }))
        );
      },
      (err) => {
        console.error(err);
      }
    );
  }, [startDate, endDate, customerId, campaignId]);
  useEffect(() => {
    loadEvents();
  }, [customerId, campaignId, startDate, endDate]);

  useEffect(() => {
    //after call outcome is setted reloads activities
    if (
      notifyEndCall &&
      notifyEndCall?.baseEntityId === customerId &&
      notifyEndCall.outcome
    ) {
      loadEvents();
    }
  }, [notifyEndCall]);

  return (
    <Slide in={open} direction="up">
      <Paper
        id="activity-progress-popup"
        elevation={6}
        sx={{
          position: 'absolute',
          overflow: 'hidden',
          display: 'flex',
          bottom: '16px',
          zIndex: 1100,
          width: sidebarOpen ? '80%' : '94%',
          transition: 'all 0.5s ease-in-out !important',
        }}
      >
        <FullSizeColumn>
          <ActivityProgressSummary
            open
            onToggle={onClose}
            status={
              activeCampaigns?.find((campaign) => campaign.id === campaignId)
                ?.dividerName
            }
            onReload={loadEvents}
          />
          <ParentSize>
            {(parent) => (
              <TimelineChart
                width={parent.width}
                height={400}
                period={periodLabel}
                startDate={startDate}
                endDate={endDate}
                onChangePeriod={handlePeriodChange}
                events={events}
              />
            )}
          </ParentSize>
        </FullSizeColumn>
      </Paper>
    </Slide>
  );
};

export default ActivityProgressPopup;
